import { useState, useCallback, useEffect } from "react";
import { TextField, Button, CircularProgress } from "@mui/material";
import styles from "./AddFamilyMemberModal.module.css";

const initialAddForm = {
  full_name: "",
  email: "",
  password: "",
  password2: "",
};

function AddTraderModal({ isOpen, onClose, onAdd, editTrader, onEdit }) {
  const isEdit = Boolean(editTrader);
  const [form, setForm] = useState(initialAddForm);
  const [editEmail, setEditEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (editTrader) {
      setEditEmail(editTrader.email || "");
    } else {
      setForm(initialAddForm);
    }
    setError("");
    setSubmitting(false);
  }, [isOpen, editTrader]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
  }, []);

  const validateAdd = useCallback(() => {
    if (!form.full_name?.trim()) return "Full name is required.";
    if (!form.email?.trim()) return "Email is required.";
    if (!form.password) return "Password is required.";
    if (!form.password2) return "Please confirm password.";
    if (form.password !== form.password2) return "Passwords do not match.";
    return null;
  }, [form]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (isEdit && onEdit) {
        if (!editEmail?.trim()) {
          setError("Email is required.");
          return;
        }
        setSubmitting(true);
        try {
          await onEdit(editTrader.id, { email: editEmail.trim() });
          setError("");
          onClose();
        } catch (err) {
          setError(err?.response?.data?.detail || "Failed to update trader");
        } finally {
          setSubmitting(false);
        }
        return;
      }

      const validationError = validateAdd();
      if (validationError) {
        setError(validationError);
        return;
      }

      setSubmitting(true);
      try {
        await onAdd({
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          password: form.password,
          password2: form.password2,
        });
        setForm(initialAddForm);
        setError("");
        onClose();
      } catch (err) {
        setError(err?.response?.data?.detail || "Failed to add trader");
      } finally {
        setSubmitting(false);
      }
    },
    [isEdit, editTrader, editEmail, onEdit, onAdd, onClose, form, validateAdd]
  );

  const handleClose = useCallback(() => {
    if (submitting) return;
    setForm(initialAddForm);
    setEditEmail("");
    setError("");
    onClose();
  }, [onClose, submitting]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={submitting ? undefined : handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>{isEdit ? "Edit trader" : "Add trader"}</h2>
          <button type="button" className={styles.closeBtn} onClick={handleClose} disabled={submitting} aria-label="Close">
            <svg className={styles.closeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}

          <fieldset disabled={submitting} className={styles.formFieldset}>
          {isEdit ? (
            <div className={styles.sectionBlock}>
              <div className={styles.field}>
                <label className={styles.label}>Email</label>
                <TextField
                  name="email"
                  type="email"
                  value={editEmail}
                  onChange={(e) => {
                    setEditEmail(e.target.value);
                    setError("");
                  }}
                  placeholder="trader@example.com"
                  fullWidth
                  size="small"
                  className={styles.input}
                  variant="outlined"
                />
              </div>
            </div>
          ) : (
            <>
              <div className={styles.sectionLabel}>Trader details</div>
              <div className={styles.sectionBlock}>
                <div className={styles.grid}>
                  <div className={styles.field}>
                    <label className={styles.label}>Full name</label>
                    <TextField
                      name="full_name"
                      value={form.full_name}
                      onChange={handleChange}
                      placeholder="e.g. Jane Trader"
                      fullWidth
                      size="small"
                      className={styles.input}
                      variant="outlined"
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>Email</label>
                    <TextField
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="e.g. jane@kniwealth.com"
                      fullWidth
                      size="small"
                      className={styles.input}
                      variant="outlined"
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>Password</label>
                    <TextField
                      name="password"
                      type="password"
                      value={form.password}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      className={styles.input}
                      variant="outlined"
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>Confirm password</label>
                    <TextField
                      name="password2"
                      type="password"
                      value={form.password2}
                      onChange={handleChange}
                      fullWidth
                      size="small"
                      className={styles.input}
                      variant="outlined"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          </fieldset>

          <div className={styles.actions}>
            <Button type="button" variant="outlined" onClick={handleClose} className={styles.cancelBtn} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className={styles.submitBtn}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {submitting ? (isEdit ? "Saving…" : "Adding…") : isEdit ? "Update" : "Add trader"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddTraderModal;
